import { PropsWithChildren } from 'react';
import { CopilotSidebar } from '@backstage-community/plugin-copilot';
import { makeStyles } from '@material-ui/core';
import {
  RiHome5Line,
  RiPlugLine,
  RiBookLine,
  RiBookOpenLine,
  RiCopyrightLine,
  RiAddCircleLine,
  RiMenuLine,
  RiSearchLine,
  RiApps2Line,
  RiTrophyLine,
  RiPuzzleLine,
} from '@remixicon/react';
import EnlivioLogo from './EnlivioLogo';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link
        to="/"
        underline="none"
        className={classes.link}
        aria-label="Enlivio Health home"
      >
        <EnlivioLogo compact={!isOpen} />
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const config = useApi(configApiRef);
  const version = config.getOptionalString('frontend.version');
  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup
          label="Search"
          icon={<RiSearchLine size={20} />}
          to="/search"
        >
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<RiMenuLine size={20} />}>
          {/* Global nav, not org-specific */}
          <SidebarItem
            icon={() => <RiHome5Line size={20} />}
            to="/"
            text="Home"
          />
          <SidebarItem
            icon={() => <RiApps2Line size={20} />}
            to="catalog"
            text="Catalog"
          />
          <SidebarItem
            icon={() => <RiPlugLine size={20} />}
            to="api-docs"
            text="APIs"
          />
          <SidebarItem
            icon={() => <RiPuzzleLine size={20} />}
            to="app-extensions"
            text="App Extensions"
          />
          <SidebarItem
            icon={() => <RiBookLine size={20} />}
            to="docs"
            text="Docs"
          />
          <SidebarItem
            icon={() => <RiTrophyLine size={20} />}
            to="maturity"
            text="Maturity"
          />
          <SidebarItem
            icon={() => <RiAddCircleLine size={20} />}
            to="create"
            text="Create..."
          />
          {/* End global nav */}
          <SidebarDivider />
          <SidebarScrollWrapper>
            <CopilotSidebar />
          </SidebarScrollWrapper>
          <SidebarDivider />
          <SidebarScrollWrapper>
            <SidebarItem
              icon={() => <RiBookOpenLine size={20} />}
              target="_blank"
              to="https://engineering.emis-x.uk"
              text="Guidance"
            />
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
          {version && (
            <SidebarItem
              icon={() => <RiCopyrightLine size={20} />}
              to={`https://github.com/emisgroup/emisx-dev-portal/releases/tag/${version}`}
              text={`Version: ${version}`}
            />
          )}
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
